import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Storefront as StorefrontIcon,
  ListAlt as ListAltIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewModule as ViewModuleIcon,
  Description as DescriptionIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import InformesBackButton from '../../components/shared/InformesBackButton';
import PDFViewer from '../../components/shared/PDFViewer';
import InformePersonalizadoParametrosDialog from '../../components/ventas/InformePersonalizadoParametrosDialog';
import { resolveInformesModuloFromPath } from '../../config/informesModulos';
import { useAuth } from '../../contexts/AuthContext';
import informesStoreService, {
  InformeParametro,
  InformeStoreCatalogItem,
  InformeStoreItem,
} from '../../services/informesStoreService';
import { getApiErrorMessage } from '../../utils/apiError';
import { isAdminRole } from '../../utils/ubicacionUsuarioRoles';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { INFORMES_PERSONALIZADOS_TOUR } from '../../config/tours/informesVentasTour';

type InformesLayoutVista = 'detalle' | 'tarjetas' | 'iconos';
type InformesSeccion = 'catalogo' | 'store';

function vistaStorageKey(pathPrefix: string) {
  return `${pathPrefix}.informesPersonalizados.vista`;
}

function vistaStorageKeyLegacy(pathPrefix: string) {
  return `${pathPrefix}.storeInformes.vista`;
}

interface InformesVistaPrefs {
  catalogo: InformesLayoutVista;
  store: InformesLayoutVista;
}

function isLayoutVista(value: unknown): value is InformesLayoutVista {
  return value === 'detalle' || value === 'tarjetas' || value === 'iconos';
}

function readVistaPrefs(pathPrefix: string): InformesVistaPrefs {
  const defaults: InformesVistaPrefs = { catalogo: 'tarjetas', store: 'tarjetas' };
  try {
    const raw = localStorage.getItem(vistaStorageKey(pathPrefix));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<InformesVistaPrefs>;
      return {
        catalogo: isLayoutVista(parsed.catalogo) ? parsed.catalogo : defaults.catalogo,
        store: isLayoutVista(parsed.store) ? parsed.store : defaults.store,
      };
    }
    const legacy = localStorage.getItem(vistaStorageKeyLegacy(pathPrefix));
    if (isLayoutVista(legacy)) {
      return { catalogo: legacy, store: legacy };
    }
  } catch {
    /* ignore */
  }
  return defaults;
}

function writeVistaPrefs(pathPrefix: string, prefs: InformesVistaPrefs) {
  try {
    localStorage.setItem(vistaStorageKey(pathPrefix), JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

const vistaButtonSx = (active: boolean) => ({
  color: active ? 'primary.main' : 'text.secondary',
  bgcolor: active ? 'primary.light' : 'transparent',
  '&:hover': { bgcolor: active ? 'primary.light' : 'action.hover' },
});

const InformesPersonalizados: React.FC = () => {
  useRegisterPageTour(INFORMES_PERSONALIZADOS_TOUR);
  const location = useLocation();
  const informesModulo = useMemo(
    () => resolveInformesModuloFromPath(location.pathname),
    [location.pathname]
  );
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const canManage = useMemo(() => isAdminRole(user?.roles), [user?.roles]);
  const [searchParams, setSearchParams] = useSearchParams();
  const seccion: InformesSeccion = searchParams.get('seccion') === 'store' ? 'store' : 'catalogo';
  const view: InformesSeccion = seccion === 'store' && canManage ? 'store' : 'catalogo';

  const setView = (next: InformesSeccion) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'store') nextParams.set('seccion', 'store');
    else nextParams.delete('seccion');
    setSearchParams(nextParams, { replace: true });
  };
  const [vistaPrefs, setVistaPrefs] = useState<InformesVistaPrefs>(() =>
    readVistaPrefs(resolveInformesModuloFromPath(window.location.pathname).pathPrefix)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [catalogo, setCatalogo] = useState<InformeStoreCatalogItem[]>([]);
  const [store, setStore] = useState<InformeStoreItem[]>([]);
  const [workingId, setWorkingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const [paramDialogOpen, setParamDialogOpen] = useState(false);
  const [paramReport, setParamReport] = useState<InformeStoreCatalogItem | null>(null);
  const [parametros, setParametros] = useState<InformeParametro[]>([]);
  const [rendering, setRendering] = useState(false);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfId, setPdfId] = useState('');

  const loadCatalogo = useCallback(async () => {
    const data = await informesStoreService.listMisInformes(informesModulo.modulo);
    setCatalogo(data);
  }, [informesModulo.modulo]);

  const loadStore = useCallback(async () => {
    if (!canManage) return;
    const data = await informesStoreService.listStore(informesModulo.modulo);
    setStore(data);
  }, [canManage, informesModulo.modulo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'store' && canManage) {
        await loadStore();
      } else {
        await loadCatalogo();
      }
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudieron cargar los informes.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [canManage, enqueueSnackbar, loadCatalogo, loadStore, view]);

  useEffect(() => {
    void load();
  }, [load]);

  const layoutVista = view === 'store' ? vistaPrefs.store : vistaPrefs.catalogo;

  useEffect(() => {
    setVistaPrefs(readVistaPrefs(informesModulo.pathPrefix));
    setSearchTerm('');
  }, [informesModulo.pathPrefix]);

  const setLayoutVista = (vista: InformesLayoutVista) => {
    setVistaPrefs((prev) => {
      const next: InformesVistaPrefs =
        view === 'store' ? { ...prev, store: vista } : { ...prev, catalogo: vista };
      writeVistaPrefs(informesModulo.pathPrefix, next);
      return next;
    });
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const storeFiltrado = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return store;
    return store.filter((item) => {
      const titulo = item.titulo.toLowerCase();
      const descripcion = (item.descripcion || '').toLowerCase();
      const codigo = item.codigo.toLowerCase();
      return titulo.includes(q) || descripcion.includes(q) || codigo.includes(q);
    });
  }, [searchTerm, store]);

  const catalogoFiltrado = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return catalogo;
    return catalogo.filter((item) => {
      const titulo = item.titulo.toLowerCase();
      const descripcion = (item.descripcion || '').toLowerCase();
      const codigo = item.codigo.toLowerCase();
      return titulo.includes(q) || descripcion.includes(q) || codigo.includes(q);
    });
  }, [catalogo, searchTerm]);

  const showPdf = (blob: Blob, title: string, id: number) => {
    const url = URL.createObjectURL(blob);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setPdfTitle(title);
    setPdfId(String(id));
  };

  const closePdf = () => {
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const executeRender = async (id: number, title: string, params?: Record<string, unknown>) => {
    setRendering(true);
    try {
      const blob = await informesStoreService.renderPdf(id, params);
      const safeBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      showPdf(safeBlob, title, id);
      setParamDialogOpen(false);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo generar el PDF del informe.'), { variant: 'error' });
    } finally {
      setRendering(false);
    }
  };

  const handleExecute = async (item: InformeStoreCatalogItem) => {
    try {
      const detail = await informesStoreService.getMisInforme(item.id);
      if (!detail.parametros?.length) {
        await executeRender(detail.id, detail.titulo);
        return;
      }
      setParamReport(detail);
      setParametros(detail.parametros);
      setParamDialogOpen(true);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo preparar la ejecución del informe.'), {
        variant: 'error',
      });
    }
  };

  const archivoFallbackName = (item: InformeStoreCatalogItem) => {
    const base = (item.codigo || item.titulo || 'informe').trim() || 'informe';
    return /\.rdlc?$/i.test(base) ? base : `${base}.rdlc`;
  };

  const handleDownload = async (item: InformeStoreCatalogItem) => {
    setDownloadingId(item.id);
    try {
      await informesStoreService.downloadArchivo(item.id, archivoFallbackName(item));
      enqueueSnackbar('Archivo del informe descargado.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo descargar el archivo del informe.'), {
        variant: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePick = async (item: InformeStoreItem) => {
    setWorkingId(item.id);
    try {
      if (item.instalado) {
        await informesStoreService.unpick(item.id);
        enqueueSnackbar('Informe quitado de la empresa.', { variant: 'success' });
      } else {
        await informesStoreService.pick(item.id);
        enqueueSnackbar('Informe instalado.', { variant: 'success' });
      }
      await loadStore();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo actualizar la instalación.'), { variant: 'error' });
    } finally {
      setWorkingId(null);
    }
  };

  const renderPickButton = (item: InformeStoreItem, opts?: { fullWidth?: boolean; size?: 'small' | 'medium' }) => (
    <Button
      variant={item.instalado ? 'outlined' : 'contained'}
      color={item.instalado ? 'error' : 'primary'}
      disabled={workingId === item.id}
      onClick={() => void handlePick(item)}
      fullWidth={opts?.fullWidth}
      size={opts?.size}
    >
      {workingId === item.id ? 'Guardando…' : item.instalado ? 'Quitar' : 'Instalar'}
    </Button>
  );

  const renderExecuteButton = (item: InformeStoreCatalogItem, opts?: { fullWidth?: boolean; size?: 'small' | 'medium' }) => (
    <Button
      variant="contained"
      startIcon={<PlayArrowIcon />}
      disabled={rendering}
      onClick={() => void handleExecute(item)}
      fullWidth={opts?.fullWidth}
      size={opts?.size}
      {...(item === catalogoFiltrado[0] ? { 'data-tour-id': 'informe-fila-acciones' } : {})}
    >
      Ejecutar
    </Button>
  );

  const renderDownloadButton = (item: InformeStoreCatalogItem, opts?: { fullWidth?: boolean; size?: 'small' | 'medium' }) => (
    <Button
      variant="outlined"
      startIcon={downloadingId === item.id ? undefined : <FileDownloadIcon />}
      disabled={downloadingId === item.id}
      onClick={() => void handleDownload(item)}
      fullWidth={opts?.fullWidth}
      size={opts?.size}
    >
      {downloadingId === item.id ? 'Descargando…' : 'Descargar'}
    </Button>
  );

  const renderVistaToolbar = () => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      data-tour-id="informe-filtros"
    >
      <TextField
        fullWidth
        placeholder="Buscar por nombre o descripción..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#1976d2' }} />
            </InputAdornment>
          ),
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          p: 0.5,
          alignSelf: { xs: 'flex-end', sm: 'center' },
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setLayoutVista('detalle')}
          sx={vistaButtonSx(layoutVista === 'detalle')}
          title="Vista detalle"
        >
          <ViewListIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setLayoutVista('tarjetas')}
          sx={vistaButtonSx(layoutVista === 'tarjetas')}
          title="Vista tarjetas"
        >
          <ViewAgendaIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setLayoutVista('iconos')}
          sx={vistaButtonSx(layoutVista === 'iconos')}
          title="Vista iconos"
        >
          <ViewModuleIcon />
        </IconButton>
      </Box>
    </Stack>
  );

  const renderStoreTarjetas = () => (
    <Grid container spacing={2}>
      {storeFiltrado.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.id}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {item.titulo}
                  </Typography>
                  <Chip size="small" label={item.planRequerido} color="success" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.descripcion || 'Sin descripción.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.versionPublicadaNumero != null ? `Versión ${item.versionPublicadaNumero}` : 'Sin versión'}
                  {item.parametrosCount > 0 ? ` · ${item.parametrosCount} parámetros` : ''}
                </Typography>
                {renderPickButton(item, { fullWidth: true })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderStoreIconos = () => (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
          xl: 'repeat(6, minmax(0, 1fr))',
        },
      }}
    >
      {storeFiltrado.map((item) => (
        <Card variant="outlined" key={item.id} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Stack spacing={1} alignItems="center">
              <DescriptionIcon sx={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={item.titulo}>
                {item.titulo}
              </Typography>
              <Chip size="small" label={item.planRequerido} color="success" variant="outlined" />
              {renderPickButton(item, { fullWidth: true, size: 'small' })}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderStoreDetalle = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Título</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell align="center">Plan</TableCell>
            <TableCell align="center">Versión</TableCell>
            <TableCell align="center">Parámetros</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {storeFiltrado.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{item.titulo}</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>{item.descripcion || '—'}</TableCell>
              <TableCell align="center">
                <Chip size="small" label={item.planRequerido} color="success" variant="outlined" />
              </TableCell>
              <TableCell align="center">
                {item.versionPublicadaNumero != null ? `v${item.versionPublicadaNumero}` : '—'}
              </TableCell>
              <TableCell align="center">{item.parametrosCount}</TableCell>
              <TableCell align="right">{renderPickButton(item, { size: 'small' })}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCatalogoTarjetas = () => (
    <Grid container spacing={2} data-tour-id="informe-resultados">
      {catalogoFiltrado.map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item.id}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {item.titulo}
                  </Typography>
                  <Chip size="small" label="Biblioteca" variant="outlined" />
                  {item.versionFijada && <Chip size="small" label="Fijada" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.descripcion || 'Sin descripción.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Versión {item.versionNumero}
                  {item.parametrosCount > 0 ? ` · ${item.parametrosCount} parámetros` : ''}
                </Typography>
                {renderExecuteButton(item, { fullWidth: true })}
                {renderDownloadButton(item, { fullWidth: true })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderCatalogoIconos = () => (
    <Box
      data-tour-id="informe-resultados"
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
          xl: 'repeat(6, minmax(0, 1fr))',
        },
      }}
    >
      {catalogoFiltrado.map((item) => (
        <Card variant="outlined" key={item.id} sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Stack spacing={1} alignItems="center">
              <DescriptionIcon sx={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={item.titulo}>
                {item.titulo}
              </Typography>
              {renderExecuteButton(item, { fullWidth: true, size: 'small' })}
              {renderDownloadButton(item, { fullWidth: true, size: 'small' })}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderCatalogoDetalle = () => (
    <TableContainer data-tour-id="informe-resultados">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Título</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell align="center">Versión</TableCell>
            <TableCell align="center">Parámetros</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {catalogoFiltrado.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{item.titulo}</span>
                  <Chip size="small" label="Biblioteca" variant="outlined" />
                  {item.versionFijada && <Chip size="small" label="Fijada" />}
                </Stack>
              </TableCell>
              <TableCell sx={{ maxWidth: 280 }}>{item.descripcion || '—'}</TableCell>
              <TableCell align="center">v{item.versionNumero}</TableCell>
              <TableCell align="center">{item.parametrosCount}</TableCell>
              <TableCell align="right">
                <Tooltip title="Ejecutar">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={rendering}
                      onClick={() => void handleExecute(item)}
                      {...(item === catalogoFiltrado[0] ? { 'data-tour-id': 'informe-fila-acciones' } : {})}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Descargar archivo">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={downloadingId === item.id}
                      onClick={() => void handleDownload(item)}
                    >
                      {downloadingId === item.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <FileDownloadIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
            >
              <Box sx={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <InformesBackButton to={informesModulo.hubPath} />
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {view === 'store' ? 'Store de informes' : informesModulo.tituloMisInformes}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {view === 'store'
                    ? 'Instalá informes Free de la biblioteca para usarlos con los datos de la empresa.'
                    : 'Ejecutá los informes instalados desde el store.'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {canManage && (
                  <Button
                    data-tour-id="informe-store"
                    variant={view === 'store' ? 'outlined' : 'contained'}
                    startIcon={view === 'store' ? <ListAltIcon /> : <StorefrontIcon />}
                    onClick={() => setView(view === 'store' ? 'catalogo' : 'store')}
                  >
                    {view === 'store' ? informesModulo.tituloMisInformes : 'Store de informes'}
                  </Button>
                )}
              </Stack>
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : view === 'store' ? (
              <Stack spacing={2}>
                {renderVistaToolbar()}
                {store.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No hay informes publicados en el store.
                  </Typography>
                ) : storeFiltrado.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No hay informes que coincidan con la búsqueda.
                  </Typography>
                ) : layoutVista === 'detalle' ? (
                  renderStoreDetalle()
                ) : layoutVista === 'iconos' ? (
                  renderStoreIconos()
                ) : (
                  renderStoreTarjetas()
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {renderVistaToolbar()}
                {catalogo.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {canManage
                      ? 'No hay informes instalados. Abrí el store para elegir los que necesita la empresa.'
                      : 'No hay informes instalados para esta empresa.'}
                  </Typography>
                ) : catalogoFiltrado.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No hay informes que coincidan con la búsqueda.
                  </Typography>
                ) : layoutVista === 'detalle' ? (
                  renderCatalogoDetalle()
                ) : layoutVista === 'iconos' ? (
                  renderCatalogoIconos()
                ) : (
                  renderCatalogoTarjetas()
                )}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <InformePersonalizadoParametrosDialog
        open={paramDialogOpen}
        titulo={paramReport?.titulo || ''}
        parametros={parametros}
        loading={rendering}
        onClose={() => {
          if (!rendering) setParamDialogOpen(false);
        }}
        onConfirm={(values) => {
          if (!paramReport) return;
          void executeRender(paramReport.id, paramReport.titulo, values);
        }}
      />

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={closePdf}
          title={pdfTitle}
          documentId={pdfId}
          documentType="informeCliente"
          downloadFileName={`${pdfTitle || 'informe'}.pdf`}
        />
      )}
    </Box>
  );
};

export default InformesPersonalizados;
